import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Award, Gift, Copy, Users, Share2, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const LEVELS = ["Explorer", "Foodie", "Super Foodie", "BiteGoFood VIP"];

export default function Rewards() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rewards").then((r) => setData(r.data)).catch(() => {}); }, []);
  const copy = (c) => { navigator.clipboard?.writeText(c); toast.success("Referral code copied"); };

  const shareMsg = (code) => `Hey! I'm loving BiteGoFood By VS-Solutions 🍔 Use my code ${code} to sign up — you get a welcome bonus and I earn rewards when you order. Download & order: ${window.location.origin}`;
  const nativeShare = async (code) => {
    const text = shareMsg(code);
    if (navigator.share) { try { await navigator.share({ title: "BiteGoFood Referral", text }); return; } catch { /* cancelled */ } }
    copy(code);
  };
  const shareWhatsApp = (code) => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg(code))}`, "_blank");
  const shareSMS = (code) => { window.location.href = `sms:?&body=${encodeURIComponent(shareMsg(code))}`; };

  const levelIdx = data ? Math.min(3, Math.floor((data.points || 0) / 250)) : 0;
  const next = (levelIdx + 1) * 250;

  return (
    <div className="min-h-screen bg-surface max-w-[520px] mx-auto pb-10">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => nav(-1)} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg">BiteGoFood Rewards</h1>
      </div>
      {!data ? <div className="p-4"><div className="skel h-40 rounded-2xl" /></div> : (
        <div className="px-4 py-4 space-y-3">
          <div className="bg-gradient-to-br from-coral to-coral-dark text-white rounded-2xl p-5">
            <div className="flex items-center gap-2"><Award size={22} /><span className="font-bold">{LEVELS[levelIdx]}</span></div>
            <div className="text-4xl font-display font-extrabold mt-2">{data.points} <span className="text-lg font-bold">pts</span></div>
            <div className="mt-3 bg-white/20 rounded-full h-2 overflow-hidden"><div className="bg-white h-full" style={{ width: `${Math.min(100, (data.points / next) * 100)}%` }} /></div>
            <div className="text-xs text-white/85 mt-1.5">{Math.max(0, next - data.points)} pts to {LEVELS[Math.min(3, levelIdx + 1)]}</div>
          </div>

          <div className="bg-white rounded-2xl border border-border p-4">
            <h3 className="font-bold text-ink flex items-center gap-2"><Users size={17} className="text-coral" /> Refer &amp; Earn</h3>
            <p className="text-sm text-ink-600 mt-1">Invite friends. They get a welcome bonus, and you earn {data.referrer_reward || 100} points when they complete their first order.</p>
            <div className="mt-3 flex items-center justify-between bg-surface rounded-xl px-3 py-3">
              <span className="font-mono font-bold text-ink tracking-widest" data-testid="referral-code">{data.referral_code}</span>
              <button onClick={() => copy(data.referral_code)} className="text-coral font-bold text-sm flex items-center gap-1" data-testid="copy-referral"><Copy size={14} /> Copy</button>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="bg-surface rounded-xl px-3 py-2.5 text-center" data-testid="referral-count"><div className="text-xl font-display font-extrabold text-coral">{data.referrals || 0}</div><div className="text-[11px] text-ink-600">Friends joined</div></div>
              <div className="bg-surface rounded-xl px-3 py-2.5 text-center"><div className="text-xl font-display font-extrabold text-veg">+{(data.referrals || 0) * (data.referrer_reward || 100)}</div><div className="text-[11px] text-ink-600">Points earned</div></div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <button onClick={() => nativeShare(data.referral_code)} className="bg-coral text-white font-bold text-sm py-2.5 rounded-xl flex items-center justify-center gap-1.5 tap" data-testid="share-invite-btn"><Share2 size={15} /> Share</button>
              <button onClick={() => shareWhatsApp(data.referral_code)} className="bg-[#25D366] text-white font-bold text-sm py-2.5 rounded-xl flex items-center justify-center gap-1.5 tap" data-testid="share-whatsapp-btn"><MessageCircle size={15} /> WhatsApp</button>
              <button onClick={() => shareSMS(data.referral_code)} className="bg-white border border-border text-ink font-bold text-sm py-2.5 rounded-xl flex items-center justify-center gap-1.5 tap" data-testid="share-sms-btn"><MessageCircle size={15} /> SMS</button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-border p-4">
            <h3 className="font-bold text-ink mb-2">Points history</h3>
            {data.history.map((h, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0"><span className="text-sm text-ink-600 flex items-center gap-2"><Gift size={15} className="text-coral" /> {h.title}</span><span className="font-bold text-veg text-sm">+{h.points}</span></div>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {LEVELS.map((l, i) => <div key={l} className={`rounded-xl p-2 text-center text-[11px] font-semibold ${i <= levelIdx ? "bg-coral text-white" : "bg-white border border-border text-ink-600"}`}>{l}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
